// =====================================================================
// teamMemberModalLogic — framework-free rendering decisions for
// TeamMemberModal.jsx, extracted for Node's built-in test runner (no JSX
// runner in this repo; mirrors pageHeaderLogic.mjs's precedent, same
// component pairing pattern).
//
// Why this exists: migrations/0023_users_email_optional_display_only.sql
// + scripts/import_team_profiles.js's --profile-only-order path can put a
// person on the public team grid with a photo and a name and NOTHING
// else — no role, no bio, no expertise, no education, no contributions
// (display_role/bio are deliberately left NULL rather than guessed).
// TeamMemberModal must not become a header floating above dead space for
// that person: every detail section already only renders when it has
// content, but with ALL of bio/expertise/education/contributions absent
// the modal used to just stop after the header, which reads as broken,
// not deliberate. This module decides (a) whether the role line should
// render at all (same "no role" case the grid card has), and (b) whether
// the detail sections have ANY content — when none do, the modal should
// show one quiet, honest "full profile not yet available" line instead
// of silently ending.
// =====================================================================

function isPresentString(value) {
  return typeof value === 'string' && value.trim().length > 0
}

function isPresentArray(value) {
  return Array.isArray(value) && value.length > 0
}

// Resolve which optional pieces of the modal should render for a given
// member (the shape GET /api/public/team returns — see
// services/auth/routes/public.js). `hasDetailContent` is true if ANY of
// bio/expertise/education/contributions is present; `showIncompleteNotice`
// is its exact negation, kept as a separate named field so the component
// reads as a decision, not an inverted boolean.
export function resolveTeamMemberModalSlots(member = {}) {
  const hasBio = isPresentString(member.bio)
  const hasExpertise = isPresentArray(member.expertise)
  const hasEducation = isPresentArray(member.education)
  const hasContributions = isPresentArray(member.contributions)
  const hasDetailContent = hasBio || hasExpertise || hasEducation || hasContributions

  return {
    hasRole: isPresentString(member.role),
    hasAffiliationBlock: isPresentString(member.affiliation) || Boolean(member.institutionLogo),
    hasLocation: isPresentString(member.location),
    hasLinkedin: isPresentString(member.linkedin),
    hasResearchProfile: isPresentString(member.researchProfile),
    hasBio,
    hasExpertise,
    hasEducation,
    hasContributions,
    hasDetailContent,
    showIncompleteNotice: !hasDetailContent,
  }
}
